
import type { Route } from 'vue-router'
import type { ErrorMessage, GuardResolveFunction, Loading } from '../models'
import { computed, defineComponent, getCurrentInstance, ref } from 'vue'
import AppHeader from '../components/app-header.component.vue'
import { UiButton, UiModal, UiSnackbarContainer } from '../components/keen-ui'
import { useAppLoading } from '../composables/use-app-loading'
import { GUARDS_EXECUTOR, LoadConfigGuard, LoginGuard } from '../guards'
import { ROUTER_CONSTS } from '../router/router.consts'

interface Error extends ErrorMessage {
  messages?: Array<string>
  primaryButtonClick?: () => void
  isCustomMessage?: boolean
}

export default defineComponent({
  name: 'Root',
  components: {
    AppHeader,
    UiSnackbarContainer,
    UiModal,
    UiButton,
  },
  beforeRouteEnter: GUARDS_EXECUTOR([LoginGuard.execute, LoadConfigGuard.execute]),
  beforeRouteUpdate(...args: [Route, Route, GuardResolveFunction]): void {
    const updateLoadingGuard = (to: Route, from: Route, next: GuardResolveFunction): void => {
      if (to.name !== from.name) {
        this.updateLoading({ visible: false })
      }
      next()
    }
    GUARDS_EXECUTOR([LoginGuard.execute, updateLoadingGuard])(...args)
  },
  setup() {
    const ctx = getCurrentInstance()

    const baseClass = 'epb-root'

    const snackbarContainer = ref()

    const errorModal = ref()

    const { loading, setLoading } = useAppLoading()

    const error = ref<Error | undefined>(undefined)

    const headerVisible = computed<boolean>(() =>
      ctx.proxy.$route.matched.every(matched => matched.name !== ROUTER_CONSTS.routes.home),
    ) // TODO Apply AppHeader to every route

    function showSnackbar(message: string): void {
      snackbarContainer.value.createSnackbar({ message })
    }

    function showError(err: Error): void {
      error.value = err
      errorModal.value.open()
    }

    function showCustomError(err: Error): void {
      showError({
        ...err,
        isCustomMessage: true,
      })
    }

    function primaryButtonEvent(): void {
      error.value.primaryButtonClick()
      errorModal.value.close()
      error.value.isCustomMessage = false
    }

    function secondaryButtonEvent(): void {
      error.value.isCustomMessage = false
      error.value.primaryButtonClick = undefined
      errorModal.value.close()
    }

    function updateLoading(value: Loading): void {
      setLoading(value.visible)
    }

    return {
      baseClass,
      snackbarContainer,
      errorModal,
      loading,
      error,
      headerVisible,
      showSnackbar,
      showError,
      showCustomError,
      primaryButtonEvent,
      secondaryButtonEvent,
      updateLoading,
    }
  },
})
